<template>
  <div v-if="authStore.isAuthenticated && authStore.user" class="relative">
    <button
      data-testid="user-menu-button"
      aria-haspopup="true"
      :aria-expanded="isMenuOpen"
      class="flex items-center space-x-2 text-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-charcoal-900 focus:ring-brand-400 rounded-full"
      @click="toggleMenu"
    >
      <img
        :src="authStore.user.picture || 'https://via.placeholder.com/40'"
        alt="User avatar"
        class="w-10 h-10 rounded-full"
      />
      <span class="hidden md:block font-medium text-charcoal-200">
        {{ authStore.user.name || authStore.user.email }}
      </span>
      <ChevronDown :size="16" class="text-charcoal-400" :stroke-width="2" />
    </button>

    <div
      v-if="isMenuOpen"
      role="menu"
      class="absolute right-0 mt-2 w-56 bg-charcoal-800 rounded-md shadow-lg ring-1 ring-charcoal-600 z-50"
    >
      <div class="px-4 py-3 border-b border-charcoal-600">
        <p class="text-sm font-medium text-white">
          {{ authStore.user.name }}
        </p>
        <p class="text-sm text-charcoal-400 truncate">
          {{ authStore.user.email }}
        </p>
      </div>

      <div class="py-1">
        <button
          class="w-full text-left px-4 py-2 text-sm text-charcoal-200 hover:bg-charcoal-700 hover:text-white flex items-center gap-2"
          role="menuitem"
          data-testid="my-profile-button"
          @click="openProfile"
        >
          <UserCircle :size="16" class="text-charcoal-400" />
          My Profile
        </button>

        <div class="border-t border-charcoal-600 my-1" />

        <router-link
          to="/"
          class="block px-4 py-2 text-sm text-charcoal-200 hover:bg-charcoal-700 hover:text-white"
          role="menuitem"
          @click="closeMenu"
        >
          Dashboard
        </router-link>

        <router-link
          to="/sites"
          class="block px-4 py-2 text-sm text-charcoal-200 hover:bg-charcoal-700 hover:text-white"
          role="menuitem"
          @click="closeMenu"
        >
          Sites & Surveys
        </router-link>

        <router-link
          v-if="
            authStore.hasRole('platform_admin') ||
            authStore.hasRole('organisation-admin') ||
            authStore.hasRole('survey-admin') ||
            authStore.hasRole('survey-builder') ||
            authStore.hasRole('appendix-report-signoff') ||
            authStore.hasRole('documents-manager') ||
            authStore.hasRole('manage-hardware') ||
            authStore.hasRole('manage-brand-standards')
          "
          to="/settings"
          class="block px-4 py-2 text-sm text-charcoal-200 hover:bg-charcoal-700 hover:text-white"
          role="menuitem"
          @click="closeMenu"
        >
          Settings
        </router-link>

        <div class="border-t border-charcoal-600 my-1" />

        <button
          data-testid="force-refresh-button"
          class="w-full text-left px-4 py-2 text-sm text-charcoal-200 hover:bg-charcoal-700 hover:text-white flex items-center gap-2"
          role="menuitem"
          title="Reload the latest version (clears cached app data)"
          @click="handleForceRefresh"
        >
          <RefreshCw :size="16" class="text-charcoal-400" />
          Refresh to latest version
        </button>

        <div class="border-t border-charcoal-600 my-1" />

        <button
          data-testid="logout-button"
          class="w-full text-left px-4 py-2 text-sm text-red-400 hover:bg-red-500/10 hover:text-red-300 flex items-center gap-2"
          role="menuitem"
          @click="handleLogout"
        >
          <LogOut :size="16" />
          Sign Out
        </button>
      </div>
    </div>
  </div>

  <UserProfileDialog v-model="profileOpen" />
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted } from 'vue'
import { useRouter } from 'vue-router'
import { useAuthStore } from '@/stores/auth.store'
import { ChevronDown, UserCircle, LogOut, RefreshCw } from 'lucide-vue-next'
import UserProfileDialog from '@/components/UserProfileDialog.vue'
import { forceRefreshApp } from '@/lib/force-refresh'

const authStore = useAuthStore()
const router = useRouter()
const isMenuOpen = ref(false)
const profileOpen = ref(false)

function toggleMenu() {
  isMenuOpen.value = !isMenuOpen.value
}

function closeMenu() {
  isMenuOpen.value = false
}

function openProfile() {
  closeMenu()
  profileOpen.value = true
}

async function handleForceRefresh() {
  closeMenu()
  await forceRefreshApp()
}

async function handleLogout() {
  closeMenu()
  const logoutUrl = import.meta.env.VITE_AUTH0_LOGOUT_URL || window.location.origin + '/login'
  await authStore.logout({ returnTo: logoutUrl })
  router.push('/login')
}

// Close menu when clicking outside
function handleClickOutside(event: MouseEvent) {
  const target = event.target as HTMLElement
  if (!target.closest('[data-testid="user-menu-button"]') && !target.closest('[role="menu"]')) {
    closeMenu()
  }
}

onMounted(() => {
  document.addEventListener('click', handleClickOutside)
})

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside)
})
</script>
